import type { FieldDiff, FieldMetadata, FieldValue, RecordRow } from '../../data/client';

/**
 * A proposal plus the record as it stands = the diff a human decides on.
 *
 * Pure, because this is the part that must be RIGHT: an approver reads these pairs and
 * clicks Approve, so a wrong "current" value is a wrong decision. Keeping it out of the
 * component means it can be tested exhaustively without a DOM.
 *
 * Fields are ordered by their position in the model, not by the order the agent happened
 * to send them, so two proposals against the same object read the same way.
 */

export interface ProposalDiff {
  readonly diff: readonly FieldDiff[];
  /**
   * INV-5: proposed fields a human owns. They are shown, and marked, and they will NOT
   * be applied - the approved write skips them and records `write_blocked`. Hiding them
   * would let an approver believe they approved something that can never land.
   */
  readonly lockedFieldIds: readonly string[];
  /** Proposed field ids with no field in the model any more. */
  readonly unknownFieldIds: readonly string[];
}

export function buildProposalDiff(
  values: Readonly<Record<string, FieldValue>>,
  current: RecordRow | null,
  fields: readonly FieldMetadata[],
): ProposalDiff {
  const proposed = new Set(Object.keys(values));
  const ordered = fields.filter((field) => proposed.has(field.id));

  return {
    diff: ordered.map((field) => ({
      fieldId: field.id,
      // A create proposal has no "current": null is the honest before-value.
      before: current?.values[field.id] ?? null,
      after: values[field.id] ?? null,
    })),
    lockedFieldIds: ordered
      .filter((field) => current?.provenance[field.id] !== undefined)
      .map((field) => field.id),
    unknownFieldIds: [...proposed].filter(
      (fieldId) => !fields.some((field) => field.id === fieldId),
    ),
  };
}

/** The record's title, by the object's label field, for the queue's target column. */
export function recordLabel(
  current: RecordRow | null,
  labelFieldId: string | undefined,
): string | null {
  if (current === null || labelFieldId === undefined) return null;
  const value = current.values[labelFieldId];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}
